/**
 * M27 task-07 — the provider picker shared by the model create/edit forms.
 * Pure props-driven select (exported test seam) over the supported LLM
 * providers (HUB-082 §M27), rendered from the shared `LLM_PROVIDERS`
 * single-home — the hub's `buildModelFromRow` switch reads the same list, so
 * this can never offer a provider the resolver cannot construct.
 *
 * The base-URL field is provider-conditional in the caller; `requiresBaseUrl`
 * (also shared, and enforced by the write route) is the one rule for which.
 */
import { LLM_PROVIDERS, type LlmProvider } from '@verybigthings/trackframe-shared/schemas';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

export const PROVIDERS = LLM_PROVIDERS;
export type Provider = LlmProvider;

/** Per-provider help for the base-URL field the caller renders. */
export const BASE_URL_HINTS: Partial<Record<Provider, { label: string; placeholder: string; hint: string }>> = {
  azure: {
    label: 'Azure endpoint',
    placeholder: 'https://my-resource.openai.azure.com',
    hint: 'The resource endpoint — its first hostname label is used as the Azure resource name.',
  },
  'openai-compatible': {
    label: 'Base URL',
    placeholder: 'https://openrouter.ai/api/v1',
    hint: 'The vendor’s OpenAI-compatible endpoint, e.g. OpenRouter or z.ai. Include the version path.',
  },
};

export interface ProviderSelectProps {
  value: Provider;
  onChange: (value: Provider) => void;
}

export function ProviderSelect({ value, onChange }: ProviderSelectProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="provider">Provider</Label>
      <Select
        id="provider"
        aria-label="Provider"
        className="w-full"
        value={value}
        onChange={(e) => onChange(e.target.value as Provider)}
      >
        {PROVIDERS.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </Select>
    </div>
  );
}
